import { Component, EventEmitter, OnInit, Output, ViewChild, Inject } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { GridDataResult, RowClassArgs } from '@progress/kendo-angular-grid';
import { NotificationService } from '@progress/kendo-angular-notification';
import { State, process, filterBy, CompositeFilterDescriptor } from '@progress/kendo-data-query';
import { Guid } from 'guid-typescript';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable } from 'rxjs';
import { map } from 'rxjs';
import { IPerformanceBodFracPond } from 'src/app/api/performance-bod-frac-pond';
import { PerformanceBodStoreService } from 'src/app/services/performance-bod-store.service';
import { IPerformanceBodPackages } from 'src/app/api/performance-bod-package';
import { PerformanceBodFracPondService } from './infrastructure-frac-pond.service';
import { UtilService } from 'src/app/services/util.service';
import { PermissionsProvider } from 'src/app/services/permissions.provider';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { DragAndDropService } from 'src/app/_shared/drag-and-drop.service';

const DRAGGABLE_SELECTOR = '.infrastructure-frac-ponds-draggable';

@Component({
    selector: 'app-infrastructure-frac-pond',
    templateUrl: './infrastructure-frac-pond.component.html',
    standalone: false
})
export class InfrastructureFracPondComponent implements OnInit {
  public filter: CompositeFilterDescriptor;
  public filterable: boolean = true;
  public messageDuplicate: boolean = true;
  public view: Observable<GridDataResult>;
  public performanceBodPackagesList: IPerformanceBodPackages[];
  public yesNoList: any[] = [{ text: 'Yes', value: true }, { text: 'No', value: false }];
  public performanceBodId: string;
  public formGroup: UntypedFormGroup;
  public gridState: State = {
    sort: [],
    skip: 0,
  };
  public canEditPerformanceBod: boolean = false;
  @Output() public performanceBodUpdate = new EventEmitter<string>();
  @Output() public saveBodCompleted = new EventEmitter<boolean>();
  public performanceBodFracPondService: PerformanceBodFracPondService;
  @ViewChild('performanceBodFracPond')
  public infrastructureFracPondGrid: any;
  private dragAndDropService: DragAndDropService;

  constructor(
    private performanceBodStoreService: PerformanceBodStoreService,
    private formBuilder: UntypedFormBuilder,
    private spinner: NgxSpinnerService,
    private notificationService: NotificationService,
    private utilService: UtilService,
    private permissionsProvider: PermissionsProvider,
    private authenticationService: AuthenticationService,
    @Inject(DragAndDropService) dragAndDropServiceFactory: any,
    @Inject(PerformanceBodFracPondService) performanceBodFracPondServiceFactory: any
  ) {
    this.dragAndDropService = dragAndDropServiceFactory();
    this.performanceBodFracPondService = performanceBodFracPondServiceFactory();
  }

  public ngOnInit(): void {   
    this.performanceBodStoreService.performanceBodSignatured$.subscribe((isSignature) => {
      this.canEditPerformanceBod = isSignature? false: this.permissionsProvider.canEditPerformanceBod;
    });

    this.performanceBodStoreService.performanceBodId$.subscribe(id => {
      this.performanceBodId = id;
      this.performanceBodFracPondService.performanceBodId = id;
      this.performanceBodFracPondService.read();
    });

    this.performanceBodStoreService.performanceBodPackagesData$.subscribe(data => {
      this.performanceBodPackagesList = data;
    });

    this.view = this.performanceBodFracPondService.pipe(
      map(data => {
        this.dragAndDropService.dataSourceRefreshed(data);
        return process(filterBy(data, this.filter), this.gridState);
      })
    );

    this.dragAndDropService.initialize(this.performanceBodFracPondService.data, DRAGGABLE_SELECTOR, () => {
      this.performanceBodFracPondService.markAsSetOrder(this.authenticationService.getCurrentUser());
    });
  }

  public ngAfterViewInit(): void {
    // To Do
  }

  public ngOnDestroy(): void {
    this.dragAndDropService.refreshDragAndDrop();
  }

  public createFormGroup(dataItem: any): UntypedFormGroup {
    return this.formBuilder.group({
      id: new UntypedFormControl(dataItem.id),
      name: new UntypedFormControl(''),
      fracPondName: new UntypedFormControl(dataItem.fracPondName, [
        Validators.required,
        Validators.minLength(1),
        Validators.maxLength(100),
      ]),
      chargeCode: new UntypedFormControl(dataItem.chargeCode, [
        Validators.maxLength(13),
        Validators.pattern('^[a-zA-Z0-9]{0,13}$'),
      ]),
      cndHOEstimate: new UntypedFormControl(dataItem.cndHOEstimate, [Validators.min(0), Validators.max(999999999999)]),
      isDeleted: new UntypedFormControl(dataItem.isDeleted),
      permitsRequiredBy: [dataItem.permitsRequiredBy],
      readyForServiceDate: new UntypedFormControl(dataItem.readyForServiceDate, Validators.required),
      newPlusExistingVolumeMbbl: new UntypedFormControl(dataItem.newPlusExistingVolumeMbbl, [
        Validators.min(0),
        Validators.max(100000),
      ]),
      comments: new UntypedFormControl(dataItem.comments, [Validators.minLength(0), Validators.maxLength(500)]),
      performanceBodPackagesId: new UntypedFormControl(dataItem.performanceBodPackagesId, Validators.required),
      afeNumber: new UntypedFormControl(dataItem.afeNumber, [
        Validators.maxLength(13),
        Validators.pattern('^[a-zA-Z0-9]{0,13}$'),
      ]),  
      afeAmount: new UntypedFormControl(dataItem.afeAmount, [
        Validators.maxLength(8),
        Validators.pattern('^[a-zA-Z0-9]{0,8}$'),
      ]), 
    });
  }

  public cellClickHandler({ sender, rowIndex, columnIndex, dataItem, isEdited }) {
    if (!isEdited && this.canEditPerformanceBod) {
      if (dataItem.readyForServiceDate !== undefined && columnIndex === 2) {
        if (dataItem.readyForServiceDate !== null) {
          const y = new Date(dataItem.readyForServiceDate.toString()).getFullYear();
          const m = new Date(dataItem.readyForServiceDate.toString()).getMonth();
          const d = new Date(dataItem.readyForServiceDate.toString()).getDate();
          dataItem.readyForServiceDate = new Date(y, m, d);
        }
      }

      if (dataItem.permitsRequiredBy !== undefined && columnIndex === 3) {
        if (dataItem.permitsRequiredBy !== null) {
          const y = new Date(dataItem.permitsRequiredBy.toString()).getFullYear();
          const m = new Date(dataItem.permitsRequiredBy.toString()).getMonth();
          const d = new Date(dataItem.permitsRequiredBy.toString()).getDate();
          dataItem.permitsRequiredBy = new Date(y, m, d);
        }
      }

      sender.editCell(rowIndex, columnIndex, this.createFormGroup(dataItem));
      this.dragAndDropService.onEditingMode();
    }
  }

  public cellCloseHandler(args: any) {
    const { formGroup, dataItem, column } = args;

    let columnField = '';

    if (column.field === 'performanceBodPackagesName') {
      columnField = 'performanceBodPackagesId';
    } else {
      columnField = column.field;
    }

    const checkColumn = formGroup.controls[columnField];

    if (!checkColumn.valid) {
      args.preventDefault();
    } else if (checkColumn.dirty) {
      if (columnField === 'performanceBodPackagesId') {
        dataItem.performanceBodPackagesName = this.getPerformanceBodPackages(formGroup.controls[columnField].value);
      }
    }

    if (!formGroup.valid) {
      // prevent closing the edited cell if there are invalid values.
      args.preventDefault();
    } else if (formGroup.dirty) {
      if (formGroup.valid && args.sender.data.total > 0) {
        for (let i = 0; i <= args.sender.data.total - 1; i++) {
          if (
            args.sender.data.data[i].id !== formGroup.value.id &&
            args.sender.data.data[i].fracPondName === formGroup.value.fracPondName &&
            args.sender.data.data[i].performanceBodPackagesId === formGroup.value.performanceBodPackagesId
          ) {
            this.messageDuplicate = false;
            args.preventDefault();
            return;
          } else {
            this.messageDuplicate = true;
          }
        }
      }

      if (this.messageDuplicate) {
        dataItem.updatedDate = new Date();
        // Date is not working in Object.assign(), so need to be do it manually
        formGroup.value.updatedBy = dataItem.updatedBy;
        formGroup.value.updatedDate = dataItem.updatedDate;

        this.performanceBodFracPondService.assignValues(dataItem, formGroup.value);
        this.performanceBodFracPondService.updateItem(dataItem);
      }
    }
    // calls this to add the attribute of current row
    this.dragAndDropService.onEditingClose();
    this.dragAndDropService.refreshDragAndDrop();
  }

  public addHandler({ sender }) {
    const item: IPerformanceBodFracPond = {
      id: Guid.create().toString(),
      name: '',
      fracPondName: '',
      isDeleted: false,
      transferPump: false,
      aeration: false,
      permitsRequiredBy: null,
      readyForServiceDate: null,
      existingUsableVolume: 0,
      newPlusExistingVolumeMbbl: 0,
      comments: null,
      performanceBodPackagesId: '',
      createdBy: this.authenticationService.getCurrentUser(),
      createdDate: new Date(),
      updatedBy: this.authenticationService.getCurrentUser(),
      updatedDate: new Date(),
      sortOrder: 0,
      chargeCode: '',
      cndHOEstimate: 0,
      afeNumber:'',
      afeAmount: 0,
    };

    item.createdBy = this.authenticationService.getCurrentUser();
    item.createdDate = new Date();
    sender.addRow(this.createFormGroup(item));
    this.dragAndDropService.onEditingMode();
  }

  public cancelHandler({ sender, rowIndex }) {
    this.messageDuplicate = true;
    sender.closeRow(rowIndex);
    this.dragAndDropService.onEditingClose();
    this.dragAndDropService.refreshDragAndDrop();
  }

  public saveHandler({ sender, formGroup, rowIndex }) {
    if (formGroup.valid && sender.data.total > 0) {
      for (let i = 0; i <= sender.data.total - 1; i++) {
        if (
          sender.data.data[i].performanceBodPackagesId === formGroup.value.performanceBodPackagesId &&
          sender.data.data[i].id !== formGroup.value.id &&
          sender.data.data[i].fracPondName === formGroup.value.fracPondName
        ) {
          this.messageDuplicate = false;
          return;
        } else {
          this.messageDuplicate = true;
        }
      }
    }
    if (this.messageDuplicate) {
      if (formGroup.valid) {
        this.performanceBodFracPondService.createItem(formGroup.value);
        sender.closeRow(rowIndex);
        this.dragAndDropService.onEditingClose();
        this.dragAndDropService.dataSourceRefreshed(this.performanceBodFracPondService.data);
        this.dragAndDropService.refreshDragAndDrop();
        this.performanceBodFracPondService.markAsSetOrder(this.permissionsProvider.cai);
      } else {
        this.utilService.markFormGroupAsTouched(formGroup);
      }
    }
  }

  public removeHandler({ sender, dataItem }) {
    dataItem.isDeleted = true;
    this.performanceBodFracPondService.removeItem(dataItem);

    sender.cancelCell();
  }

  public saveChanges(grid: any): void {
    grid.closeCell();
    grid.cancelCell();
    this.spinner.show();
    // calls this to add the attribute of current row
    this.dragAndDropService.refreshDragAndDrop();
    this.performanceBodFracPondService.saveChanges();
    this.performanceBodUpdate.next(this.authenticationService.getCurrentUser());

    setTimeout(() => {
      this.spinner.hide();
      this.notificationService.show({
        content: 'Infrastructure - Frac Pond saved',
        position: { horizontal: 'center', vertical: 'top' },
        animation: { type: 'fade', duration: 500 },
        type: { style: 'success', icon: true },
        hideAfter: 700,
      });
    }, 3000);
  }

  public saveFromBOD(): void {
    this.infrastructureFracPondGrid.closeCell();
    this.infrastructureFracPondGrid.cancelCell();

    if (this.performanceBodFracPondService.hasChanges()) {
      this.spinner.show();
      // calls this to add the attribute of current row
      this.dragAndDropService.refreshDragAndDrop();
      this.performanceBodFracPondService.saveChanges();
      this.performanceBodUpdate.next(this.authenticationService.getCurrentUser());
      const subscription = this.performanceBodFracPondService.subscribe(() => {
        this.saveBodCompleted.next(true);
      });

      subscription.unsubscribe();
    }
  }

  public cancelChanges(grid: any): void {
    this.messageDuplicate = true;
    grid.cancelCell();
    this.dragAndDropService.refreshDragAndDrop();
    this.performanceBodFracPondService.cancelChanges();
  }

  public onStateChange(state: State) {
    this.gridState = state;
    this.performanceBodFracPondService.read();

    // const viewSubscription = this.view.subscribe(data => {
    //   this.dragAndDropService.dataSourceRefreshed(data.data);
    // });

    // viewSubscription.unsubscribe();
    // this.dragAndDropService.runZoneOnStable();
  }

  public getPerformanceBodPackages(id: string): any {
    if (this.performanceBodPackagesList === null || this.performanceBodPackagesList === undefined) {
      return '';
    }
    this.performanceBodPackagesList = this.performanceBodPackagesList.filter(x => x.id !== null && x.id !== undefined);
    return this.performanceBodPackagesList.find(x => x.id === id);
  }

  public getYesNo(option: boolean): any {
    return this.yesNoList.find(x => x.value === option);
  }

  public handlerFilter(filter: CompositeFilterDescriptor): void {
    this.filter = filter;
    if (filter.filters) {
      if (filter.filters.length > 0) {
        this.dragAndDropService.onEditingMode();
      } else {
        this.dragAndDropService.onEditingClose();
        this.dragAndDropService.refreshDragAndDrop();
      }
    }
  }

  public rowCallback(context: RowClassArgs) {
    return {
      dragging: context.dataItem.dragging,
    };
  }

  public rowLoaded(): void {
    // to check until grid have been loeaded.
    this.dragAndDropService.checkIsOnEditMode();
    this.dragAndDropService.checkDraggableElementLoaded();
  }
}
